/**
 * Weekly Briefings reducer.
 *
 * Two slices keyed by project_id (string):
 *   list   — summary rows from /briefings/list (NO briefing_json)
 *   detail — keyed by briefing.id; full rows including briefing_json,
 *            populated lazily by /briefings/get or by an on-demand
 *            generate that returned the row inline
 *
 * Splitting list vs detail mirrors the backend payload split: the list
 * endpoint intentionally omits briefing_json (~5–10 KB per row) to keep
 * the history page light, and the detail endpoint loads the JSON only
 * for the window the user actually opens.
 */

import {
  SET_BRIEFINGS_LIST,
  SET_BRIEFING_DETAIL,
  SET_BRIEFINGS_LOADING,
  SET_BRIEFINGS_ERROR,
  SET_BRIEFING_GENERATING,
  CLEAR_ALL,
} from '../constants';
import type { BriefingDetail, BriefingSummary } from '../actions/briefings';

interface BriefingsState {
  /** Per-project summary rows, newest first. */
  list: Record<string, BriefingSummary[]>;
  /** Full briefing rows keyed by briefing id. */
  detail: Record<number, BriefingDetail>;
  /** Per-project loading flag for the list fetch. */
  loading: Record<string, boolean>;
  /** Per-project error string from the most recent fetch (cleared on success). */
  error: Record<string, string | null>;
  /** Per-project flag toggled while an on-demand generate call is in flight. */
  generating: Record<string, boolean>;
}

const initialState: BriefingsState = {
  list: {},
  detail: {},
  loading: {},
  error: {},
  generating: {},
};

export default function briefingsReducer(
  state = initialState,
  { type, payload }: { type: string; payload?: any },
): BriefingsState {
  switch (type) {
    case CLEAR_ALL:
      return initialState;

    case SET_BRIEFINGS_LIST: {
      const pid = String(payload.project_id);
      const briefings: BriefingSummary[] = Array.isArray(payload.briefings) ? payload.briefings : [];
      // Detail rows we already have for this project (if any) stay cached;
      // the list refresh just re-keys the summaries. The detail map is keyed
      // by briefing.id so it won't collide.
      return {
        ...state,
        list: { ...state.list, [pid]: briefings },
      };
    }

    case SET_BRIEFING_DETAIL: {
      const briefing: BriefingDetail | undefined = payload?.briefing;
      if (!briefing || typeof briefing.id !== 'number') return state;
      const pid = String(briefing.project_id);
      // Mirror the (possibly newer) summary into the per-project list. This
      // makes "Generate now" feel instantaneous: the briefing appears in the
      // history pane the moment the detail comes back, without a second
      // round-trip to /briefings/list.
      const summary: BriefingSummary = {
        id: briefing.id,
        period_start: briefing.period_start,
        period_end: briefing.period_end,
        status: briefing.status,
        change_score: briefing.change_score,
        headline: briefing.headline,
        summary_text: briefing.summary_text,
        generated_at: briefing.generated_at,
        created_at: briefing.created_at,
      };
      const existingList = state.list[pid] || [];
      const idx = existingList.findIndex((b) => b.id === briefing.id);
      let nextList: BriefingSummary[];
      if (idx >= 0) {
        nextList = existingList.slice();
        nextList[idx] = summary;
      } else {
        // Insert in period_start-desc order (newest first).
        nextList = [summary, ...existingList].sort((a, b) =>
          (b.period_start || '').localeCompare(a.period_start || ''),
        );
      }
      return {
        ...state,
        detail: { ...state.detail, [briefing.id]: briefing },
        list: { ...state.list, [pid]: nextList },
      };
    }

    case SET_BRIEFINGS_LOADING:
      return {
        ...state,
        loading: { ...state.loading, [String(payload.project_id)]: !!payload.loading },
      };

    case SET_BRIEFINGS_ERROR:
      return {
        ...state,
        error: { ...state.error, [String(payload.project_id)]: payload.error ?? null },
      };

    case SET_BRIEFING_GENERATING:
      return {
        ...state,
        generating: { ...state.generating, [String(payload.project_id)]: !!payload.generating },
      };

    default:
      return state;
  }
}
